import { DefaultPropertyColor, PropertyColor } from '@thiememeulenhoff/types';
import { html, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import { TmElement } from '../../custom-element.helper';
import { Listen, ListenHandle } from '../../event.helper';

export abstract class TmFormElement extends TmElement {
  #formData: ListenHandle | null = null;

  @query('#inputElement')
  input?: HTMLInputElement;

  type = 'text';

  @property({ type: String })
  name?: string;

  @property({ type: String })
  placeholder?: string;

  @property({ type: Boolean, reflect: true })
  disabled?: boolean;

  @property({ type: Boolean })
  required?: boolean;

  @property({ type: Boolean })
  readonly?: boolean;

  @property({ type: String, reflect: true })
  color: PropertyColor = DefaultPropertyColor;

  @property({ type: Boolean, reflect: true })
  indeterminate = false;

  @property({ type: Boolean, reflect: true })
  resizable = false;

  #value = '';

  #checked = false;

  @property({ reflect: true })
  get value() {
    if (this.input) {
      return this.input.value;
    }

    return this.#value;
  }

  set value(value) {
    const oldValue = this.#value;
    this.#value = value;

    this.requestUpdate('value', oldValue);
    if (this.input) {
      this.input.value = value as string;
    }
  }

  @property({ type: Boolean, reflect: true })
  get checked() {
    if (this.input) {
      return this.input.checked;
    }

    return this.#checked;
  }

  set checked(checked) {
    const oldValue = this.#checked;
    this.#checked = checked;

    this.requestUpdate('checked', oldValue);
    if (this.input) {
      this.input.checked = checked;
    }
  }

  protected inputTemplate() {
    return html`<input
      id="inputElement"
      name=${this.name ?? nothing}
      type=${this.type}
      placeholder=${this.placeholder ?? nothing}
      ?disabled=${this.disabled}
      ?required=${this.required}
      ?readonly=${this.readonly}
      .indeterminate=${live(this.indeterminate)}
      .checked=${this.checked}
      .value=${this.value}
      @input=${this.handleInput}
      @change=${this.handleChange}
      aria-label="Input element"
    />`;
  }

  protected handleInput(event: Event) {
    this.#value = (event.target as HTMLInputElement).value;
    this.handleChange(event);
  }

  protected handleChange(event: Event) {
    this.indeterminate = false;

    if (this.input) {
      this.checked = this.input.checked;
    }

    this.#value = (event.target as HTMLInputElement).value;
    this.dispatchEvent(new Event(event.type, event));
  }

  override connectedCallback() {
    super.connectedCallback();

    const form = this.closest('form');
    if (form) {
      this.#formData = Listen(form, 'formdata', this.handleFormData.bind(this));
    }
  }

  override disconnectedCallback() {
    if (this.#formData) {
      this.#formData = this.#formData.release();
    }

    super.disconnectedCallback();
  }

  override render() {
    return html`
      <label for="inputElement">
        <slot></slot>
      </label>
      <div class="input">${this.inputTemplate()}</div>
    `;
  }

  handleFormData(event: Event) {
    const { formData } = event as FormDataEvent;
    const { disabled, name, value } = this;

    if (!disabled) {
      formData.append(name ?? '', value as string);
    }
  }
}
